import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { type CSSProperties, type JSX, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';

import type { FilterAPI } from '~/api/Catalog';
import { company } from '~/company/Company';
import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import { EmptyWithFilters } from '~/components/EmptyWithFilters';
import Icon from '~/components/Icon/Icon';
import { MobileFilters } from '~/components/MobileFilters';
import MobileSorting from '~/components/MobileSorting/MobileSorting';
import Pagination from '~/components/Pagination/Pagination';
import ProductBadgesList from '~/components/ProductBadge/ProductBadgesList';
import ProductCard from '~/components/ProductCard/ProductCard';
import ProductFilter from '~/components/ProductFilter/ProductFilter';
import ProductSorting from '~/components/ProductSorting/ProductSorting';
import SkeletonListRows from '~/components/Skeleton/SkeletonList';
import { useGlobal } from '~/hooks/useGlobal';
import { useHeaderSize } from '~/hooks/useHeaderSize';
import { useSticky } from '~/hooks/useSticky';
import { DescriptionBlock } from '~/pages/Catalog/DescriptionBlock';
import EmptyState from '~/pages/Catalog/EmptyState';
import { InfinityScrollLoader } from '~/pages/Catalog/InfinityScrollLoader';
import type { GetProductFilterOptions } from '~/pages/Catalog/interfaces';
import NotFound from '~/pages/NotFound/NotFound';

import { catalogStore, orderStore } from '~/stores';

import MobileHeaderLayout from '../Main/MobileHeader/MobileHeaderLayout';
import MobileHeaderTitle from '../Main/MobileHeader/MobileHeaderTitle';

import styles from './Catalog.module.scss';
import { CategorySkeleton } from './CatalogSkeleton';
import FilterSkeleton from './FilterSkeleton';
import { useCategoryPage } from './hooks';

const Catalog = () => {
  const { t } = useTranslation();
  const { isMobile } = useGlobal();

  const {
    category,
    mobileSort,
    mobileFilters,
    filters,
    expressDeliveryFilterItem,
    parent,
    backLink,
    pageTitle,
    pageDescription,
    canonical,
    robots,
    requestPage,
    loadMore,
    handleResetFilters,
    handleChangeFilter,
    handleApplyMobileFilters,
    handleApplySorting,
    handleApplyBadges,
    handleRemoveBadges,
    getAppliedFilters,
  } = useCategoryPage();

  const innerAsideRef = useRef<HTMLDivElement>(null);
  const outerAsideRef = useRef<HTMLDivElement>(null);
  const { headerHeight } = useHeaderSize();

  useSticky({
    topSpacing: headerHeight,
    outerRef: outerAsideRef,
    innerRef: innerAsideRef,
  });

  const getProductFilters = (
    filters: FilterAPI[],
    options?: GetProductFilterOptions,
  ): JSX.Element => {
    const { className, noCollapsible, mobile } = options || {};

    return (
      <>
        {filters.map((filter) => (
          <ProductFilter
            key={filter.code}
            onChange={(code, value) => handleChangeFilter(code, value, mobile)}
            filter={filter}
            selectedFilters={catalogStore.currentFilters}
            className={className}
            noCollapsible={noCollapsible}
          />
        ))}
      </>
    );
  };

  const descriptionBlockWithSorting = (
    <div className={styles.saasDescription}>
      {category?.data && (
        <DescriptionBlock
          title={category.data.name}
          text={category.data.description}
          results={`${category.paginationRequest.total || 0} results`}
        />
      )}
      {!isMobile && (
        <ProductSorting
          value={`${catalogStore.currentSorting[0]}.${catalogStore.currentSorting[1]}`}
          onChange={handleApplySorting}
        />
      )}
    </div>
  );

  return (
    <div
      className={classes(['page-catalog', styles.pageCatalog])}
      data-company={company.name}
    >
      <Helmet>
        <title>
          {pageTitle}
          {company.config.name}
        </title>
        <meta name="description" content={pageDescription} />
        <meta data-react-helmet="true" name="robots" content={robots} />
        <link data-react-helmet="true" rel="canonical" href={canonical} />
      </Helmet>
      <MobileHeaderLayout
        content={
          <MobileHeaderTitle
            text={category.data?.name ?? ''}
            classNames={styles.mobileHeader}
          />
        }
        toolbarClassName={styles.toolbarContent}
      />
      <div className="content-layout">
        {category.isNotFound ? (
          <NotFound embedded />
        ) : (
          <>
            {!(
              company.isDefaultHeaderInCatalog && category.data?.description
            ) &&
              company.variant({
                '2': (
                  <div className={styles.breadcrumbs}>
                    {!category.data ? (
                      <div className={styles.breadcrumbsSkeletonContainer}>
                        <Skeleton className={styles.breadcrumbsSkeleton} />
                      </div>
                    ) : (
                      <Breadcrumbs items={category.data?.breadcrumbs ?? []} />
                    )}
                  </div>
                ),
              })}
            {company.isDefaultHeaderInCatalog &&
              !isMobile &&
              descriptionBlockWithSorting}
            <div className="two-columns">
              <div ref={outerAsideRef}>
                <aside className="two-columns__filters" ref={innerAsideRef}>
                  <>
                    {(parent || backLink) && (
                      <Link
                        to={parent ? `/c/${parent}` : `${backLink}`}
                        className="two-columns__filters-back"
                      >
                        <Icon type="arrow" size={16} />
                        {t('back')}
                      </Link>
                    )}
                    {!!category.types.length && (
                      <>
                        {!!category.types.length && (
                          <>
                            {!company.isDefaultHeaderInCatalog && (
                              <p className="two-columns__filters-title">
                                {category.data?.name}
                              </p>
                            )}
                            <div className="two-columns__filters-types">
                              {category.types.map(({ value, label }) => (
                                <Link
                                  key={label}
                                  state={{ parent: category.data?.id }}
                                  to={`/c/${value}`}
                                  className="two-columns__filters-link"
                                >
                                  {label}
                                </Link>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </>
                  <p className="two-columns__filters-header">{t('filter')}</p>
                  {getProductFilters([
                    {
                      code: 'price',
                      name: 'Price',
                      showExpanded: true,
                      type: 'selector',
                      values: [],
                    },
                  ])}
                  {category.data?.filters.length ? (
                    getProductFilters(filters)
                  ) : category.paginationRequest.isLoading ? (
                    <FilterSkeleton />
                  ) : null}
                  <div className={styles.resetAllButtonContainer}>
                    <button
                      type="button"
                      className={classes([
                        'button _bordered',
                        styles.resetAllButton,
                      ])}
                      onClick={() => handleResetFilters()}
                    >
                      {t('resetAll')}
                    </button>
                  </div>
                </aside>
              </div>
              <div className="two-columns__content">
                {!company.isDefaultHeaderInCatalog &&
                  !isMobile &&
                  category.data?.description && (
                    <DescriptionBlock
                      title={category.data.name}
                      text={category.data.description}
                    />
                  )}
                {!company.isDefaultHeaderInCatalog && (
                  <div className={classes(['block-title-wrap', styles.title])}>
                    <div className="container-flex _v-baseline">
                      {!category.data?.description && (
                        <h1
                          className={classes(['block-title', styles.pageTitle])}
                        >
                          {category.data?.name}
                        </h1>
                      )}
                      <div
                        className={classes([
                          'block-title__sup',
                          styles.resultsCount,
                        ])}
                      >
                        {category.paginationRequest.total ?? 0}&nbsp;
                        {t('results')}
                      </div>
                    </div>
                    <ProductSorting
                      value={`${catalogStore.currentSorting[0]}.${catalogStore.currentSorting[1]}`}
                      onChange={handleApplySorting}
                    />
                  </div>
                )}
                {!isMobile ? (
                  catalogStore.currentBadges.length > 0 && (
                    <ProductBadgesList
                      badgesViewedCount={5}
                      onSelect={handleApplyBadges}
                      onUnselect={handleRemoveBadges}
                      currentBadges={catalogStore.currentBadges}
                    />
                  )
                ) : category.data ? (
                  category.types.length > 1 && (
                    <div className="filter-bages-container">
                      <div className="filter-bages">
                        <div
                          className={styles.types}
                          data-company={company.name}
                        >
                          {category.types.map(({ label, value }) => (
                            <Link
                              key={value}
                              state={{ parent: category.data?.id }}
                              to={`/c/${value}`}
                              className={styles.mobileTypeLink}
                            >
                              {label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="filter-bages-container">
                    <div className="filter-bages">
                      <SkeletonListRows rows={5}>
                        <Skeleton className="control__radio skeleton" />
                      </SkeletonListRows>
                    </div>
                  </div>
                )}
                {company.isDefaultHeaderInCatalog &&
                  isMobile &&
                  category.data?.description &&
                  descriptionBlockWithSorting}
                <div className={styles.mobileCatalogFilterTriggers}>
                  {expressDeliveryFilterItem && (
                    <ProductFilter
                      onChange={handleChangeFilter}
                      filter={expressDeliveryFilterItem}
                      selectedFilters={catalogStore.currentFilters}
                      className={styles.mobileExpressDelivery}
                      isCompact
                    />
                  )}
                  <div className={styles.mobileFilterRightCorner}>
                    <span
                      className={classes([
                        styles.mobileFilterTrigger,
                        getAppliedFilters() > 0 && styles.active,
                      ])}
                      role="button"
                      onClick={mobileFilters.open}
                    >
                      <Icon
                        type="filters"
                        size={24}
                        className={classes(
                          getAppliedFilters() > 0 && styles.activeIcon,
                        )}
                      />
                      {getAppliedFilters() > 0 && (
                        <div className={styles.filterCounter}>
                          {getAppliedFilters()}
                        </div>
                      )}
                    </span>
                    <span
                      className={styles.mobileFilterTrigger}
                      role="button"
                      onClick={mobileSort.open}
                    >
                      <Icon type="sort" size={24} />
                    </span>
                  </div>
                </div>
                {!company.isDefaultHeaderInCatalog &&
                  isMobile &&
                  category.data?.description && (
                    <DescriptionBlock
                      title={category.data.name}
                      text={category.data.description}
                      results={`${
                        category.paginationRequest.total || 0
                      } results`}
                    />
                  )}
                {category.data && category.paginationRequest.isLoading && (
                  <div
                    className="loader"
                    style={
                      {
                        '--header-height': `${headerHeight}px`,
                      } as CSSProperties
                    }
                  >
                    <div className="spinner" />
                  </div>
                )}
                {!category.data ? (
                  <div data-company={company.name} className="block-products">
                    <CategorySkeleton num={16} />
                  </div>
                ) : category.isNotFound ? (
                  <EmptyState />
                ) : (category.products?.length ?? 0) === 0 ? (
                  <EmptyWithFilters onReset={() => handleResetFilters()} />
                ) : (
                  <>
                    <div data-company={company.name} className="block-products">
                      {(category.products ?? []).map((product, index) => (
                        <ProductCard
                          key={`${product.id}${index}`}
                          product={product}
                          variant={company.productCardVariantL}
                        />
                      ))}
                    </div>
                    {company.variant({
                      '1': category.paginationRequest.canLoadMore && (
                        <InfinityScrollLoader
                          onLoadMore={() => {
                            category.paginationRequest.debounceLoadMore(
                              orderStore.etaWarehouseCode,
                            );
                            category.paginationRequest.setStatus('fulfilled');
                          }}
                        />
                      ),
                      '2': (
                        <Pagination
                          showButton
                          showPaginator
                          pageTotal={category.paginationRequest.pageTotal}
                          pageCurrent={category.paginationRequest.currentPage}
                          requestPage={requestPage}
                          loadMore={loadMore}
                        />
                      ),
                    })}
                  </>
                )}
              </div>

              <MobileFilters
                open={mobileFilters.visible}
                onClose={mobileFilters.close}
                onApply={handleApplyMobileFilters}
                onReset={() => handleResetFilters({ mobile: true })}
                results={category.paginationRequest.total ?? 0}
              >
                <>
                  {getProductFilters(
                    [
                      {
                        code: 'price',
                        name: 'Price',
                        showExpanded: true,
                        type: 'selector',
                        values: [],
                      },
                    ],
                    {
                      className: styles.mobileFiltersRow,
                      mobile: true,
                    },
                  )}
                  {getProductFilters(
                    filters.filter(
                      (item) =>
                        item.code !== 'express_delivery' &&
                        isMobile &&
                        item.code !== 'type',
                    ) || [],
                    {
                      className: styles.mobileFiltersRow,
                      mobile: true,
                    },
                  )}
                </>
              </MobileFilters>
              <MobileSorting
                open={mobileSort.visible}
                onClose={mobileSort.close}
                value={`${catalogStore.currentSorting[0]}.${catalogStore.currentSorting[1]}`}
                onChange={handleApplySorting}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default observer(Catalog);
